import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';
import NotesListing from '@cont/Store/Notes/NotesListing';
import Layout from '@layout/index';
import {
  fetchPostSubNotesDetails,
  fetchPreSubNotesDetails
} from '@api/fetchers/store';
import { fetchSubscriptionFaqs } from '@api/fetchers/checkout';
import { BLOCK_TYPE } from '@cont/Store/Notes/constants';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsUserSubscribedToStoreBundle } from '@api/selectors/subscription';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import SEO from '@seo/index';
import { generateFAQ } from '@seo/scholarship/FAQ';

const Notes = ({ fallbackData, pageSeo = {} }) => {
  const { notesData, faqBlock } = fallbackData;
  const { title, description, canonicalUrl, faqSchema } = pageSeo;
  const [data, setData] = useState([...notesData, faqBlock]);

  const {
    query: { noteUID }
  } = useRouter();

  const hasActiveNotesSubscription = useMyInfo(
    getIsUserSubscribedToStoreBundle(noteUID, '600')
  );
  const { refetchMyInfo } = useMyInfo();

  useEffect(() => {
    refetchMyInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hasActiveNotesSubscription) {
      fetchPostSubNotesDetails(noteUID).then((res) => {
        setData([...res.slice(1), res[0], faqBlock]);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasActiveNotesSubscription, noteUID]);

  return (
    <>
      <SEO
        title={title}
        description={description}
        url={canonicalUrl}
        openGraph={{
          title,
          description,
          url: canonicalUrl
        }}
        schema={faqSchema}
      />
      <NotesListing data={data} />;
    </>
  );
};

export const getStaticProps = async ({ params: query }) => {
  try {
    const notesData = await fetchPreSubNotesDetails(query.noteUID);
    let faqsData = [];

    if (notesData.error) {
      return { notFound: true, revalidate: 60 };
    }

    const isDigitalNotesPresent = notesData.some(
      (block) => block.blockType === BLOCK_TYPE.DIGITAL
    );
    const isPhysicalNotesPresent = notesData.some(
      (block) => block.blockType === BLOCK_TYPE.PHYSICAL
    );

    if (isDigitalNotesPresent && !isPhysicalNotesPresent) {
      faqsData = await fetchSubscriptionFaqs('digitalnotes');
    } else if (!isDigitalNotesPresent && isPhysicalNotesPresent) {
      faqsData = await fetchSubscriptionFaqs('physicalnotes');
    } else {
      faqsData = await fetchSubscriptionFaqs('notesCombo');
    }

    const faqsList = faqsData.faqs || [];
    const faqBlock = {
      blockType: 'faqs',
      blockData: faqsData.faqs
    };

    const faqSchema = generateFAQ(faqsList);

    const combo = notesData.find(
      (item) => item.blockType === BLOCK_TYPE.ABOUT
    )?.blockData;
    const title = `${combo?.title
      ?.replace(/\s*\([^)]*\)\s*$/, '')
      .trim()}| Unacademy`;
    const description = combo?.description?.slice(0, 160);
    const noteUID = combo?.uid;
    const slug = query?.slug;
    const canonicalUrl = `https://unacademy.com/store/notes/${slug}/${noteUID}`;

    return {
      props: {
        fallbackData: {
          notesData,
          faqBlock
        },
        pageSeo: {
          title,
          description,
          canonicalUrl,
          faqSchema
        }
      },
      revalidate: 5 * 60
    };
  } catch {
    return { notFound: true, revalidate: 60 };
  }
};

Notes.pageInfo = {
  name: 'Notes',
  isMWebSupported: true,
  layoutOptions: {
    useLightBaseColor: true
  }
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};

export default Notes;

Notes.Layout = Layout;
Notes.Wrapper = PageReduxProvider;
